import {
  type Env,
  ENVS,
  type Partition,
  PARTITIONS,
  PLATFORMS,
  BC_HOSTNAMES,
  BIM360_HOSTNAMES,
  BIM360_EMEA_HOSTNAMES,
  BIM360_FEDRAMP_HOSTNAMES,
  PARTITION_DOMAINS,
  COMMERCIAL_EU_DOMAIN,
  OXYGEN_URL,
  USER_PROFILE_URL,
  APIGEE_URL,
  REPORTS_V2_URL,
  FORMS_API_URL,
  ADMIN_API_URL,
  ADMIN_CONSTRUCTION_API_URL,
  BIM360_ADMIN_URL,
  RELATIONSHIP_API_URL,
} from './envConsts';
import { parseURL } from './parseURL';

const getSafeEnvFromProcessEnv = () => {
  try {
    return process.env.REACT_APP_ACS_ENV;
  } catch {
    return undefined;
  }
};

const getSafeRegionFromProcessEnv = () => {
  try {
    return process.env.REACT_APP_ACS_REGION;
  } catch {
    return undefined;
  }
};

function isBIM(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.QA]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_FEDRAMP_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_FEDRAMP_HOSTNAMES[ENVS.PRODUCTION])
  );
}

function isBIM_QA(currentHostname: string) {
  return currentHostname.includes(BIM360_HOSTNAMES[ENVS.QA]);
}

function isBIM_STAGING(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_FEDRAMP_HOSTNAMES[ENVS.STAGING])
  );
}

function isBIM_PRODUCTION(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_FEDRAMP_HOSTNAMES[ENVS.PRODUCTION])
  );
}

function isBIM_US(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.QA]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.PRODUCTION])
  );
}

function isBIM_EMEA(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.STAGING])
  );
}

function isBIM_FedRAMP(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_FEDRAMP_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_FEDRAMP_HOSTNAMES[ENVS.STAGING])
  );
}

function isACC_Commercial(currentHostname: string) {
  return (
    currentHostname.includes('acc') &&
    (currentHostname.includes(PARTITION_DOMAINS[PARTITIONS.COMMERCIAL]) ||
      currentHostname.includes(COMMERCIAL_EU_DOMAIN))
  );
}

function isCPA(currentHostname: string) {
  // Match `cpa` only as a hostname label (optionally env-suffixed, e.g.
  // `cpa-staging`) rather than an arbitrary substring, and require a
  // commercial domain — mirroring `isACC_Commercial` above.
  return (
    /(?:^|\.)cpa(?:-[a-z0-9]+)?\./.test(currentHostname) &&
    (currentHostname.includes(PARTITION_DOMAINS[PARTITIONS.COMMERCIAL]) ||
      currentHostname.includes(COMMERCIAL_EU_DOMAIN))
  );
}

function isGovCloud(currentHostname: string) {
  return currentHostname.includes(PARTITION_DOMAINS[PARTITIONS.GOVCLOUD]);
}

function isBuildingConnected(currentHostname: string) {
  return (
    currentHostname.includes(BC_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BC_HOSTNAMES[ENVS.PRODUCTION])
  );
}

/**
 * Returns one of `PLATFORMS` values based on the current hostname.
 * Fallback to `ACC` if not exist.
 */
export function getPlatformFromUrl() {
  if (isBIM(window.location.hostname)) {
    return PLATFORMS.BIM360;
  } else if (isCPA(window.location.hostname)) {
    return PLATFORMS.CPA;
  }

  return PLATFORMS.ACC;
}

let regionFromUrl: string | undefined;

export const getRegionFromUrl = (url?: string): string | undefined => {
  if (!url && regionFromUrl) return regionFromUrl;

  const currentHostname = url ?? window.location.hostname;

  if (!currentHostname) {
    return regionFromUrl;
  }

  // BuildingConnected supports US region only; CPA falls through to parseURL,
  // which extracts its region from the hostname like ACC.
  if (isBuildingConnected(currentHostname)) {
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    return 'US';
  }

  const platform = getPlatformFromUrl();

  if (platform === PLATFORMS.BIM360) {
    if (isBIM_US(currentHostname)) {
      // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
      regionFromUrl = 'US';
    }

    if (isBIM_EMEA(currentHostname)) {
      // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
      regionFromUrl = 'EMEA';
      return 'EMEA'; // eslint-disable-line @adsk/r13n-best-practices/no-region-string-literal
    }
  } else {
    const { region } = parseURL(currentHostname);
    regionFromUrl = region;
    return region;
  }

  return regionFromUrl;
};

let partitionFromUrl: Partition | undefined;

// https://autodesk.atlassian.net/wiki/spaces/CAS/pages/326730407/Regionalization+Standard#Concept:-Autodesk-Partition
export const getPartitionFromUrl = (url?: string): Partition | undefined => {
  if (!url && partitionFromUrl) return partitionFromUrl;

  const currentHostname = url ?? window.location.hostname;

  if (!currentHostname) {
    return partitionFromUrl;
  }

  let partition: Partition;

  if (isBIM_FedRAMP(currentHostname)) {
    // the legacy Bim360 FedRAMP partition https://autodesk.atlassian.net/wiki/spaces/ACSFedRAMP
    partition = PARTITIONS.FEDRAMP_LEGACY;
  } else if (isGovCloud(currentHostname)) {
    // the AfG GovCloud partition https://autodesk.atlassian.net/wiki/spaces/R13N/pages/682788475/AfG+Decision+AfG+on+GovCloud+to+be+a+NEW+Autodesk+Partition
    partition = PARTITIONS.GOVCLOUD;
  } else if (
    isBIM(currentHostname) ||
    isBuildingConnected(currentHostname) ||
    isACC_Commercial(currentHostname) ||
    isCPA(currentHostname)
  ) {
    partition = PARTITIONS.COMMERCIAL;
  } else {
    console.error(
      `Unable to determine partition from URL: ${currentHostname}. Returning undefined`,
    );
    return undefined;
  }

  // Only populate the module-level cache when deriving from window.location
  // (no explicit url). Passing an explicit hostname is a pure, side-effect-free
  // lookup and must not poison the cache used by the no-arg form.
  if (!url) {
    partitionFromUrl = partition;
  }
  return partition;
};

export const clearRegionFromUrl = (): void => {
  regionFromUrl = undefined;
};

export const clearPartitionFromUrl = (): void => {
  partitionFromUrl = undefined;
};

interface EnvironmentConfig {
  env?: Env;
  region?: string;
  partition?: Partition;
}

export const EnvironmentConfigSymbol = Symbol('EnvironmentConfig');

/**
 * Returns one of `PARTITIONS` values. Prefers an explicit override set via
 * `setPartition`, falling back to detection from the current hostname.
 * Returns `undefined` if the partition cannot be determined.
 */
export const getPartition = (): Partition | undefined => {
  return window[EnvironmentConfigSymbol]?.partition ?? getPartitionFromUrl();
};

/**
 * Determines whether the current partition is the GovCloud partition.
 *
 * @returns `true` if the partition is `GOVCLOUD`; otherwise, `false`.
 */
export const isGovPartition = (): boolean => {
  return getPartition() === PARTITIONS.GOVCLOUD;
};

declare global {
  interface Window {
    /** @deprecated */
    REACT_APP_ACS_REGION: string;
    /** @deprecated */
    REACT_APP_ACS_ENV: string;

    [EnvironmentConfigSymbol]?: EnvironmentConfig;
  }
}

/**
 * Returns string of a region according to environment variable or detect it from URL.
 * Fallback to `US` if not exist.
 */
export const getRegion = (): string => {
  const fromWindow = window.REACT_APP_ACS_REGION;
  const fromEnv = getSafeRegionFromProcessEnv();

  return (
    window[EnvironmentConfigSymbol]?.region ??
    // eslint-disable-next-line @adsk/r13n-best-practices/no-default-region, @adsk/r13n-best-practices/no-region-string-literal
    (fromWindow || fromEnv || getRegionFromUrl() || 'US').toUpperCase()
  );
};

/**
 * Maps current region to lowercase URL 3 letters region code
 */
const getRegionCode = (region: string): string => {
  const regionUpper = region.toUpperCase();
  // Legacy regions
  if (
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    regionUpper === 'US'
  ) {
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    return 'usa';
  }
  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  if (regionUpper === 'EMEA') {
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    return 'irl';
  }
  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  if (regionUpper === 'APAC') {
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    return 'aus';
  }
  // New regions
  return regionUpper.toLowerCase();
};

let envFromUrl: Env | undefined;

export const getEnvFromUrl = (url?: string): Env | undefined => {
  if (!url && envFromUrl) {
    return envFromUrl;
  }

  const currentHostname = url ?? window.location.hostname;
  if (!currentHostname) {
    return envFromUrl;
  }

  if (currentHostname.includes(BC_HOSTNAMES[ENVS.STAGING])) {
    envFromUrl = ENVS.STAGING;
    return ENVS.STAGING;
  }

  if (currentHostname.includes(BC_HOSTNAMES[ENVS.PRODUCTION])) {
    envFromUrl = ENVS.PRODUCTION;
    return ENVS.PRODUCTION;
  }

  const platform = getPlatformFromUrl();
  if (platform === PLATFORMS.BIM360) {
    if (isBIM_QA(currentHostname)) {
      envFromUrl = ENVS.QA;
      return ENVS.QA;
    }

    if (isBIM_STAGING(currentHostname)) {
      envFromUrl = ENVS.STAGING;
      return ENVS.STAGING;
    }

    if (isBIM_PRODUCTION(currentHostname)) {
      envFromUrl = ENVS.PRODUCTION;
      return ENVS.PRODUCTION;
    }
  } else {
    const { env } = parseURL(currentHostname);
    envFromUrl = env;
    return env;
  }

  return envFromUrl;
};

export const clearEnvFromUrl = (): void => {
  envFromUrl = undefined;
};

/**
 * Returns one of `ENVS` values according to environment variable.
 * Fallback to `QA` if not exist.
 */
export const getEnv = (): Env => {
  const fromWindow = window.REACT_APP_ACS_ENV;
  const fromEnv = getSafeEnvFromProcessEnv();

  const env =
    window[EnvironmentConfigSymbol]?.env ??
    (fromWindow || fromEnv || getEnvFromUrl() || ENVS.QA).toUpperCase();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!Object.values(ENVS).includes(env as any)) {
    throw Error(
      `Unknown 'env' value: ${fromEnv}. Must be one of: ${Object.values(ENVS)}`,
    );
  }
  return env as Env;
};

export const getBim360Hostname = ({ region }: overrideProps = {}) => {
  const env = getEnv();
  const finalRegion = region || getRegion();

  // Only US and EMEA are valid regions for BIM 360
  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  const validRegions = ['US', 'EMEA'];
  if (
    !validRegions.includes(finalRegion) ||
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    (env === ENVS.QA && finalRegion !== 'US')
  ) {
    throw new Error(
      `unable to load hostname for region ${finalRegion}, env ${env}`,
    );
  }

  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  if (finalRegion === 'US') {
    return BIM360_HOSTNAMES[env];
  }

  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  if (finalRegion === 'EMEA') {
    return BIM360_EMEA_HOSTNAMES[env];
  }

  return BIM360_HOSTNAMES[env];
};

type overrideProps = {
  region?: string;
  partition?: Partition;
  env?: Env;
};

/**
 * Returns the AFG GovCloud ACC hostname (on `autodesk.us`). Assumes the caller has
 * already confirmed the GovCloud partition via {@link isGovPartition}.
 *
 * GovCloud is US-only with prod/staging hosts: `forma.usa.autodesk.us` (prod) and
 * `forma.stg.usa.autodesk.us` (staging). QA is not a GovCloud env, so any non-staging
 * env collapses to the prod host.
 */
const getGovHostname = ({ region, env }: overrideProps = {}): string => {
  const finalEnv = env || getEnv();
  const finalRegion = region || getRegion();
  const govDomain = PARTITION_DOMAINS[PARTITIONS.GOVCLOUD];
  const stgPart = finalEnv === ENVS.STAGING ? 'stg.' : '';
  return `forma.${stgPart}${getRegionCode(finalRegion)}.${govDomain}`.toLowerCase();
};

export function getAccHostname({ region, env, partition }: overrideProps = {}) {
  const finalEnv = env || getEnv();
  const finalRegion = region || getRegion();
  const finalPartition = partition || getPartition();
  const envPart = finalEnv !== ENVS.PRODUCTION ? `-${finalEnv}` : '';
  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  const regionPart = finalRegion !== 'US' ? `${finalRegion}.` : '';

  if (finalPartition === PARTITIONS.GOVCLOUD) {
    return getGovHostname({ region: finalRegion, env: finalEnv });
  }

  // QA should support US region only
  if (finalEnv === ENVS.QA) {
    return `acc${envPart}.autodesk.com`.toLowerCase();
  }

  if (
    finalEnv === ENVS.STAGING &&
    // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
    !['US', 'EMEA', 'APAC', 'AUS'].includes(finalRegion)
  ) {
    return `acc.stg.${regionPart}autodesk.com`.toLowerCase();
  }

  // EMEA has a different domain
  // eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
  if (finalRegion === 'EMEA') {
    return `acc${envPart}.autodesk.eu`.toLowerCase();
  }

  return `acc${envPart}.${regionPart}autodesk.com`.toLowerCase();
}

export function getCPAHostname({ region, env, partition }: overrideProps = {}) {
  return getAccHostname({ region, env, partition }).replace('acc', 'cpa');
}

export const getCPAOrigin = ({ region }: overrideProps = {}) => {
  return `https://${getCPAHostname({ region })}`;
};

export const getAccOrigin = ({ region }: overrideProps = {}) => {
  return `https://${getAccHostname({ region })}`;
};

export const getBim360Origin = ({ region }: overrideProps = {}) => {
  return `https://${getBim360Hostname({ region })}`;
};

export const getLoginUrl = () => {
  const platform = getPlatformFromUrl();

  if (platform === PLATFORMS.BIM360) {
    return `https://login.${getBim360Hostname()}`;
  }

  return `https://login.${getAccHostname()}`;
};

export const getApigeeRegionalOrigin = ({
  region,
  partition,
}: overrideProps = {}) => {
  const env = getEnv();
  const finalRegion = region || getRegion();
  const regionCode = getRegionCode(finalRegion);
  const envPart = env === ENVS.PRODUCTION ? '' : '.stg';
  const finalPartition = partition || getPartition() || PARTITIONS.COMMERCIAL;
  const partitionDomain = PARTITION_DOMAINS[finalPartition];

  if (finalPartition === PARTITIONS.FEDRAMP_LEGACY) {
    // this is not compliant with the r13n standard but required for backwards compatibility
    return `https://api${env === ENVS.PRODUCTION ? '' : '-stg'}.aps.${partitionDomain}`;
  }
  return `https://api.aps${envPart}.${regionCode}.${partitionDomain}`;
};

export const getApigeeUrl = () => {
  // GovCloud has no legacy Apigee host, so it always resolves to the regional
  // origin; every other partition uses the legacy global Apigee host. This
  // getter never returns the regional origin for non-GovCloud partitions —
  // callers that want the regional endpoint (WPX-3700) call
  // getApigeeRegionalOrigin() directly.
  if (isGovPartition()) {
    return getApigeeRegionalOrigin();
  }
  return APIGEE_URL[getEnv()];
};

// Web Platform service getters use the Apigee regional origin in every env and
// partition (WPX-3700) — `getApigeeRegionalOrigin()` resolves the FedRAMP-legacy
// and GovCloud hosts internally, so no per-partition branch is needed here.
export const getOxygenUrl = () => OXYGEN_URL[getEnv()];
export const getUserProfileUrl = () => USER_PROFILE_URL[getEnv()];
export const getBim360AdminUrl = () =>
  `${getApigeeRegionalOrigin()}/${BIM360_ADMIN_URL[getEnv()]}`;
export const getAdminApiUrl = () =>
  `${getApigeeRegionalOrigin()}/${ADMIN_API_URL[getEnv()]}`;
export const getAdminConstructionApiUrl = () =>
  `${getApigeeRegionalOrigin()}/${ADMIN_CONSTRUCTION_API_URL[getEnv()]}`;
export const getRelationshipServiceUrl = () =>
  `${getApigeeRegionalOrigin()}/${RELATIONSHIP_API_URL[getEnv()]}`;
// Reports v2 stays on getApigeeUrl() (legacy global host, or the regional
// origin for GovCloud) in every env — it does not use the regional endpoints
// (WPX-3700).
export const getReportsV2Url = () =>
  `${getApigeeUrl()}/${REPORTS_V2_URL[getEnv()]}`;
export const getFormsUrl = () =>
  `${getApigeeRegionalOrigin()}/${FORMS_API_URL[getEnv()]}`;
